// src/pages/laporan/LaporanCreate.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Save, Send, Upload, X,
  FileText, AlertTriangle, Target, Paperclip, Eye,
  CheckCircle, Info, Clock, Loader2, RefreshCw
} from 'lucide-react';
import { Button, Card, Input, Select, Textarea, Breadcrumb, Badge, Modal } from '../../components/ui';
import { KategoriMasalah, StandarSPMI, IndikatorSPMI, TingkatRisiko, Periode } from '../../types';
import { laporanService } from '../../services/laporan';
import { adminService } from '../../services/admin';
import { getErrorMessage, logError } from '../../utils/errorHandler';
import toast from 'react-hot-toast';
import { useSystemSettingsStore } from '../../store/systemSettingsStore';
import { useAuthStore } from '../../store/authStore';

const STEPS = [
  { id: 1, title: 'Informasi Dasar', icon: <FileText className="w-4 h-4" /> },
  { id: 2, title: 'Referensi SPMI', icon: <Target className="w-4 h-4" /> },
  { id: 3, title: 'Detail Masalah', icon: <AlertTriangle className="w-4 h-4" /> },
  { id: 4, title: 'Dokumen Pendukung', icon: <Paperclip className="w-4 h-4" /> },
  { id: 5, title: 'Klasifikasi & Review', icon: <Eye className="w-4 h-4" /> },
];

interface FormData {
  kategori_masalah_id: number | null;
  standar_spmi_id: number | null;
  indikator_spmi_id: number | null;
  periode_id: number | null;
  judul: string;
  deskripsi: string;
  dampak: string;
  akar_masalah: string;
  tingkat_risiko: TingkatRisiko | '';
  target_selesai: string;
}

interface UploadedFile {
  id: string;
  file: File;
  name: string;
  size: number;
  type: string;
  preview?: string;
}

const LaporanCreate: React.FC = () => {
  const navigate = useNavigate();
  const { settings, fetchPublicSettings } = useSystemSettingsStore();
  const { user } = useAuthStore();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [draftId, setDraftId] = useState<number | null>(null);
  const draftIdRef = useRef<number | null>(null);
  const isCreatingRef = useRef(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationMessages, setValidationMessages] = useState<string[]>([]);

  // Auto-save timer states
  const AUTO_SAVE_INTERVAL = 30;
  const [autoSaveCountdown, setAutoSaveCountdown] = useState(AUTO_SAVE_INTERVAL);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // Master data from API
  const [kategoriList, setKategoriList] = useState<KategoriMasalah[]>([]);
  const [standarList, setStandarList] = useState<StandarSPMI[]>([]);
  const [indikatorList, setIndikatorList] = useState<IndikatorSPMI[]>([]);
  const [periodeList, setPeriodeList] = useState<Periode[]>([]);
  const [loadingMaster, setLoadingMaster] = useState(true);

  const [formData, setFormData] = useState<FormData>({
    kategori_masalah_id: null, standar_spmi_id: null, indikator_spmi_id: null, periode_id: null,
    judul: '', deskripsi: '', dampak: '', akar_masalah: '',
    tingkat_risiko: '', target_selesai: '',
  });

  const [files, setFiles] = useState<UploadedFile[]>([]);

  // Load master data
  useEffect(() => {
    const loadMasterData = async () => {
      try {
        setLoadingMaster(true);
        const [katRes, stdRes, indRes, perRes] = await Promise.all([
          adminService.kategori.getAll().catch(() => []),
          adminService.standar.getAll().catch(() => []),
          adminService.indikator.getAll().catch(() => []),
          adminService.periode.getAll().catch(() => []),
        ]);
        const kat = Array.isArray(katRes) ? katRes : ((katRes as any)?.data || []);
        const std = Array.isArray(stdRes) ? stdRes : ((stdRes as any)?.data || []);
        const ind = Array.isArray(indRes) ? indRes : ((indRes as any)?.data || []);
        const per = Array.isArray(perRes) ? perRes : ((perRes as any)?.data || []);
        setKategoriList(kat);
        setStandarList(std);
        setIndikatorList(ind);
        setPeriodeList(per);
        // Auto-select active periode
        const activePeriode = per.find((p: Periode) => p.is_active);
        if (activePeriode) {
          setFormData(prev => ({ ...prev, periode_id: activePeriode.id }));
        }
      } catch (err) { console.error('Failed to load master data:', err); }
      finally { setLoadingMaster(false); }
    };
    loadMasterData();
  }, []);
 
   // Fetch SLA settings
   useEffect(() => {
     fetchPublicSettings();
   }, [fetchPublicSettings]);
 
   // Guard: Admin tidak boleh buat laporan
   useEffect(() => {
     const userRole = user?.role?.name?.toLowerCase() || '';
     const allowedRoles = ['kaprodi', 'kalem', 'kabag', 'ka_tu', 'staff'];
 
     if (user && !allowedRoles.includes(userRole)) {
       toast.error('Anda tidak memiliki akses untuk membuat laporan');
       navigate('/laporan');
     }
   }, [user, navigate]);

  const filteredIndikator = indikatorList.filter(
    (ind) => ind.standar_spmi_id === formData.standar_spmi_id
  );

  // Auto-save logic with status feedback
  const autoSave = useCallback(async () => {
    if (!formData.judul.trim() || submitting) return;
    if (isCreatingRef.current) return;
    
    setSaving(true);
    setAutoSaveStatus('saving');
    
    try {
      if (draftIdRef.current) {
        await laporanService.update(draftIdRef.current, { ...formData } as any);
      } else {
        isCreatingRef.current = true;
        try {
          const res = await laporanService.create({ ...formData, status: 'draft' } as any);
          const data = (res as any)?.data || res;
          if (data?.id) {
            draftIdRef.current = data.id;
            setDraftId(data.id);
          }
        } finally {
          isCreatingRef.current = false;
        }
      }
      setLastSaved(new Date());
      setAutoSaveStatus('saved');
      setAutoSaveCountdown(AUTO_SAVE_INTERVAL);
      
      setTimeout(() => setAutoSaveStatus('idle'), 3000);
    } catch (e) {
      setAutoSaveStatus('error');
      setTimeout(() => setAutoSaveStatus('idle'), 5000);
    } finally {
      setSaving(false);
    }
  }, [formData, submitting, AUTO_SAVE_INTERVAL]);

  // Countdown timer for auto-save
  useEffect(() => {
    const hasJudul = formData.judul.trim().length > 0;
    setAutoSaveEnabled(hasJudul);
    
    if (!hasJudul) {
      setAutoSaveCountdown(AUTO_SAVE_INTERVAL);
      return;
    }

    const countdownInterval = setInterval(() => {
      setAutoSaveCountdown(prev => {
        if (prev <= 1) {
          autoSave();
          return AUTO_SAVE_INTERVAL;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [formData.judul, autoSave, AUTO_SAVE_INTERVAL]);

  const updateField = (field: keyof FormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => { const next = { ...prev }; delete next[field]; return next; });
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};
    if (step === 1) {
      if (!formData.kategori_masalah_id) newErrors.kategori_masalah_id = 'Kategori wajib dipilih';
      if (!formData.judul.trim()) newErrors.judul = 'Judul wajib diisi';
    }
    if (step === 3) {
      if (!formData.deskripsi.trim()) newErrors.deskripsi = 'Deskripsi wajib diisi';
    }
    if (step === 5) {
      if (!formData.tingkat_risiko) newErrors.tingkat_risiko = 'Tingkat risiko wajib dipilih';
      if (!formData.target_selesai) newErrors.target_selesai = 'Target selesai wajib diisi';
      if (formData.target_selesai) {
        const target = new Date(`${formData.target_selesai}T00:00:00`);
        const besok = new Date();
        besok.setHours(0, 0, 0, 0);
        besok.setDate(besok.getDate() + 1);
        if (target < besok) {
          newErrors.target_selesai = 'Tanggal target minimal besok';
        }
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const fieldLabelMap: Record<string, string> = {
    kategori_masalah_id: 'Kategori masalah',
    standar_spmi_id: 'Standar SPMI',
    indikator_spmi_id: 'Indikator SPMI',
    periode_id: 'Periode',
    judul: 'Judul laporan',
    deskripsi: 'Deskripsi masalah',
    dampak: 'Dampak',
    akar_masalah: 'Akar masalah',
    tingkat_risiko: 'Tingkat risiko',
    target_selesai: 'Tanggal target',
    files: 'Dokumen pendukung',
  };

  const translateValidationMessage = (field: string, rawMessage: string): string => {
    const message = rawMessage || '';
    const fieldLabel = fieldLabelMap[field] || field.replaceAll('_', ' ');
    const lowerMessage = message.toLowerCase();

    if (
      field === 'target_selesai'
      && (
        lowerMessage.includes('after today')
        || lowerMessage.includes('after_or_equal')
        || lowerMessage.includes('greater than')
      )
    ) {
      return 'Tanggal target minimal besok.';
    }
    if (lowerMessage.includes('required')) {
      return `${fieldLabel} wajib diisi.`;
    }
    if (lowerMessage.includes('must be a date')) {
      return `${fieldLabel} harus berupa tanggal yang valid.`;
    }
    if (lowerMessage.includes('invalid') || lowerMessage.includes('does not exist')) {
      return `${fieldLabel} tidak valid.`;
    }
    if (lowerMessage.includes('max') && lowerMessage.includes('file')) {
      return `${fieldLabel} melebihi batas ukuran yang diperbolehkan.`;
    }

    return `${fieldLabel}: ${message}`;
  };

  const extractValidationMessages = (error: any): string[] => {
    const apiErrors = error?.response?.data?.errors;
    if (!apiErrors || typeof apiErrors !== 'object') {
      return [];
    }

    const messages: string[] = [];
    Object.entries(apiErrors).forEach(([field, fieldMessages]) => {
      if (!Array.isArray(fieldMessages)) return;
      fieldMessages.forEach((msg) => {
        messages.push(translateValidationMessage(field, String(msg)));
      });
    });

    return Array.from(new Set(messages));
  };

  const showValidationErrorModal = (error: any): boolean => {
    const messages = extractValidationMessages(error);
    if (messages.length === 0) {
      const statusCode = Number(error?.response?.status || 0);
      const responseMessage = String(error?.response?.data?.message || '').toLowerCase();
      if (statusCode === 422 && responseMessage.includes('invalid field')) {
        setValidationMessages(['Terdapat field yang tidak valid. Silakan periksa kembali semua isian formulir.']);
        setShowValidationModal(true);
        return true;
      }
      return false;
    }

    setValidationMessages(messages);
    setShowValidationModal(true);
    return true;
  };

  const getBesokDateForInput = (): string => {
    const besok = new Date();
    besok.setDate(besok.getDate() + 1);
    const year = besok.getFullYear();
    const month = String(besok.getMonth() + 1).padStart(2, '0');
    const day = String(besok.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const nextStep = () => { if (validateStep(currentStep)) setCurrentStep((prev) => Math.min(prev + 1, 5)); };
  const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 1));

  const handleFileAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files || []);
    const maxFiles = 10;
    const maxSize = 10 * 1024 * 1024;
    if (files.length + newFiles.length > maxFiles) { toast.error(`Maksimal ${maxFiles} file`); return; }
    const validFiles: UploadedFile[] = [];
    for (const file of newFiles) {
      if (file.size > maxSize) { toast.error(`File "${file.name}" melebihi 10MB`); continue; }
      validFiles.push({
        id: Math.random().toString(36).substr(2, 9), file, name: file.name,
        size: file.size, type: file.type,
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
      });
    }
    setFiles((prev) => [...prev, ...validFiles]);
    e.target.value = '';
  };

  const removeFile = (id: string) => setFiles((prev) => prev.filter((f) => f.id !== id));
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleSubmit = async () => {
    if (!validateStep(5)) return;
    setSubmitting(true);
    try {
      let laporanId = draftIdRef.current;

      // Tunggu jika auto-save sedang membuat draft
      let waitCount = 0;
      while (isCreatingRef.current && waitCount < 10) {
        await new Promise(r => setTimeout(r, 200));
        waitCount++;
      }
      // Cek lagi setelah menunggu
      laporanId = draftIdRef.current;

      if (laporanId) {
        await laporanService.update(laporanId, formData as any);
      } else {
        // Buat baru hanya jika belum ada draft sama sekali
        isCreatingRef.current = true;
        try {
          const res = await laporanService.create(formData as any);
          const data = (res as any)?.data || res;
          laporanId = data.id;
          draftIdRef.current = laporanId;
          setDraftId(laporanId);
        } finally {
          isCreatingRef.current = false;
        }
      }
      // Upload files
      for (const f of files) {
        await laporanService.uploadAttachment(laporanId!, f.file);
      }
      await laporanService.submit(laporanId!);
      toast.success('Laporan berhasil disubmit!');
      navigate('/laporan');
    } catch (e: any) {
      if (showValidationErrorModal(e)) {
        logError('LaporanCreate.handleSubmit.validation', e);
        return;
      }
      const message = getErrorMessage(e, 'Gagal mengirim laporan');
      toast.error(message);
      logError('LaporanCreate.handleSubmit', e);
    }
    finally { setSubmitting(false); }
  };

  const handleSaveDraft = async () => {
    if (isCreatingRef.current) return;
    setSaving(true);
    try {
      let currentDraftId = draftIdRef.current;
      if (currentDraftId) {
        await laporanService.update(currentDraftId, formData as any);
      } else {
        isCreatingRef.current = true;
        try {
          const res = await laporanService.create({ ...formData, status: 'draft' } as any);
          const data = (res as any)?.data || res;
          if (data?.id) {
            currentDraftId = data.id;
            draftIdRef.current = data.id;
            setDraftId(data.id);
          }
        } finally {
          isCreatingRef.current = false;
        }
      }
      if (currentDraftId) {
        for (const f of files) {
          await laporanService.uploadAttachment(currentDraftId, f.file);
        }
      }
      toast.success('Draft berhasil disimpan');
      setLastSaved(new Date());
    } catch (e: any) {
      if (showValidationErrorModal(e)) {
        logError('LaporanCreate.handleSaveDraft.validation', e);
        return;
      }
      const message = getErrorMessage(e, 'Gagal menyimpan draft');
      toast.error(message);
      logError('LaporanCreate.handleSaveDraft', e);
    }
    finally { setSaving(false); }
  };

  const selectedKategori = kategoriList.find((k) => k.id === formData.kategori_masalah_id);
  const selectedStandar = standarList.find((s) => s.id === formData.standar_spmi_id);
  const selectedIndikator = indikatorList.find((i) => i.id === formData.indikator_spmi_id);
 
   // Dynamic SLA dari settings (dengan fallback)
   const slaConfig = {
     rendah: settings.sla_rendah || 60,
     sedang: settings.sla_sedang || 30,
     tinggi: settings.sla_tinggi || 14,
     kritis: settings.sla_critical || 7,
   };
 
   const risikoLabels: Record<string, { label: string; color: string; sla: string; slaDays: number; bg: string; border: string; activeBg: string; activeBorder: string; dot: string }> = {
     rendah: { 
       label: 'Rendah', 
       color: 'text-success', 
       sla: `${slaConfig.rendah} hari`, 
       slaDays: slaConfig.rendah, 
       bg: '#ecfdf5', 
       border: '#a7f3d0', 
       activeBg: '#d1fae5', 
       activeBorder: '#10b981', 
       dot: '#10b981' 
     },
     sedang: { 
       label: 'Sedang', 
       color: 'text-warning', 
       sla: `${slaConfig.sedang} hari`, 
       slaDays: slaConfig.sedang, 
       bg: '#fffbeb', 
       border: '#fde68a', 
       activeBg: '#fef3c7', 
       activeBorder: '#f59e0b', 
       dot: '#f59e0b' 
     },
     tinggi: { 
       label: 'Tinggi', 
       color: 'text-orange-500', 
       sla: `${slaConfig.tinggi} hari`, 
       slaDays: slaConfig.tinggi, 
       bg: '#fff7ed', 
       border: '#fed7aa', 
       activeBg: '#ffedd5', 
       activeBorder: '#f97316', 
       dot: '#f97316' 
     },
     kritis: { 
       label: 'Kritis', 
       color: 'text-danger', 
       sla: `${slaConfig.kritis} hari`, 
       slaDays: slaConfig.kritis, 
       bg: '#fef2f2', 
       border: '#fecaca', 
       activeBg: '#fee2e2', 
       activeBorder: '#ef4444', 
       dot: '#ef4444' 
     },
   };

  const handleRiskChange = (risk: TingkatRisiko) => {
    updateField('tingkat_risiko', risk);
    const config = risikoLabels[risk];
    if (config) {
      const target = new Date();
      target.setDate(target.getDate() + config.slaDays);
      const year = target.getFullYear();
      const month = String(target.getMonth() + 1).padStart(2, '0');
      const day = String(target.getDate()).padStart(2, '0');
      updateField('target_selesai', `${year}-${month}-${day}`);
    }
  };

  const AutoSaveIndicator = () => {
    if (!autoSaveEnabled) {
      return (
        <div className="flex items-center gap-2 text-xs text-neutral-text-secondary">
          <Clock className="w-3.5 h-3.5" />
          <span>Isi judul untuk mengaktifkan auto-save</span>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-3">
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
          autoSaveStatus === 'saving' 
            ? 'bg-blue-50 text-blue-600 border border-blue-200' 
            : autoSaveStatus === 'saved'
            ? 'bg-green-50 text-green-600 border border-green-200'
            : autoSaveStatus === 'error'
            ? 'bg-red-50 text-red-600 border border-red-200'
            : 'bg-neutral-bg text-neutral-text-secondary border border-neutral-border'
        }`}>
          {autoSaveStatus === 'saving' ? (
            <>
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              <span>Menyimpan...</span>
            </>
          ) : autoSaveStatus === 'saved' ? (
            <>
              <CheckCircle className="w-3.5 h-3.5" />
              <span>Tersimpan</span>
            </>
          ) : autoSaveStatus === 'error' ? (
            <>
              <AlertTriangle className="w-3.5 h-3.5" />
              <span>Gagal</span>
            </>
          ) : (
            <>
              <RefreshCw className="w-3.5 h-3.5" />
              <span>Auto-save: {autoSaveCountdown}s</span>
            </>
          )}
        </div>

        {lastSaved && (
          <div className="flex items-center gap-1.5 text-xs text-neutral-text-secondary">
            <Clock className="w-3 h-3" />
            <span>
              {lastSaved.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          </div>
        )}

        {draftId && (
          <div className="px-2 py-1 bg-teal-50 text-teal-600 text-[10px] font-mono rounded border border-teal-200">
            Draft #{draftId}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Laporan Masalah', href: '/laporan' }, { label: 'Buat Laporan' }]} />

      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight">Buat Laporan Masalah</h1>
          <p className="text-sm text-neutral-text-secondary mt-1">Isi formulir berikut untuk melaporkan masalah evaluasi</p>
        </div>
        <AutoSaveIndicator />
      </div>
      <Card padding="sm">
        <div className="flex items-center justify-between">
          {STEPS.map((step, index) => (
            <React.Fragment key={step.id}>
              <button onClick={() => { if (step.id < currentStep) setCurrentStep(step.id); }}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${step.id === currentStep ? 'bg-teal-main text-white shadow-md' : step.id < currentStep ? 'bg-emerald-100 text-emerald-700 cursor-pointer' : 'text-neutral-text-secondary'}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step.id === currentStep ? 'bg-white/20' : step.id < currentStep ? 'bg-emerald-200 text-emerald-700' : 'bg-neutral-bg'}`}>
                  {step.id < currentStep ? <CheckCircle className="w-4 h-4" /> : step.id}
                </div>
                <span className="hidden md:inline">{step.title}</span>
              </button>
              {index < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 rounded ${step.id < currentStep ? 'bg-success' : 'bg-neutral-border'}`} />}
            </React.Fragment>
          ))}
        </div>
      </Card>
      <Card>
        {currentStep === 1 && (
          <div className="space-y-6">
            <div className="flex items-center gap-2 text-teal-dark mb-2"><FileText className="w-5 h-5" /><h2 className="text-lg font-bold">Informasi Dasar</h2></div>
            <p className="text-sm text-neutral-text-secondary">Pilih kategori masalah dan berikan judul yang jelas.</p>
            <div className="space-y-2">
              <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">Kategori Masalah <span className="text-danger">*</span></label>
              {loadingMaster ? <p className="text-sm text-neutral-text-secondary">Memuat kategori...</p> : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {kategoriList.map((kat) => (
                    <button key={kat.id} onClick={() => updateField('kategori_masalah_id', kat.id)}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${formData.kategori_masalah_id === kat.id ? 'border-teal-main bg-teal-main/5 shadow-md' : 'border-neutral-border hover:border-teal-light hover:bg-neutral-bg'}`}>
                      <div className="flex items-center gap-3"><div className="w-3 h-3 rounded-full" style={{ backgroundColor: kat.color }} /><span className="font-semibold text-sm text-neutral-text-primary">{kat.nama}</span></div>
                      <p className="text-xs text-neutral-text-secondary mt-2">{kat.deskripsi}</p>
                    </button>
                  ))}
                </div>
              )}
              {errors.kategori_masalah_id && <p className="text-xs text-danger">{errors.kategori_masalah_id}</p>}
            </div>
            <Select
              label="Periode"
              options={periodeList.map((p) => ({ value: p.id, label: `${p.nama} ${p.is_active ? '(Aktif)' : ''}` }))}
              placeholder="Pilih periode"
              value={formData.periode_id ?? ''}
              onChange={(v) => updateField('periode_id', v ? Number(v) : null)}
            />
            <Input label="Judul Laporan" placeholder="Contoh: Kekurangan ruang laboratorium komputer di Prodi TI" value={formData.judul} onChange={(e) => updateField('judul', e.target.value)} error={errors.judul} required maxLength={500} />
            <p className="text-xs text-neutral-text-secondary text-right">{formData.judul.length}/500</p>
          </div>
        )}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div className="flex items-center gap-2 text-teal-dark mb-2"><Target className="w-5 h-5" /><h2 className="text-lg font-bold">Referensi SPMI</h2></div>
            <div className="flex items-start gap-2 p-3 bg-info/5 border border-info/20 rounded-xl"><Info className="w-5 h-5 text-info shrink-0 mt-0.5" /><p className="text-sm text-info">Bagian ini opsional, namun sangat disarankan untuk mengaitkan laporan dengan standar SPMI.</p></div>
            <Select label="Standar SPMI" options={standarList.map((s) => ({ value: s.id, label: `${s.kode} - ${s.nama}` }))} placeholder="Pilih standar SPMI (opsional)" value={formData.standar_spmi_id ?? ''} onChange={(v) => { updateField('standar_spmi_id', v ? Number(v) : null); updateField('indikator_spmi_id', null); }} />
            {formData.standar_spmi_id && (
              <Select label="Indikator SPMI" options={filteredIndikator.map((i) => ({ value: i.id, label: `${i.kode} - ${i.nama} (Target: ${i.target_value})` }))} placeholder="Pilih indikator (opsional)" value={formData.indikator_spmi_id ?? ''} onChange={(v) => updateField('indikator_spmi_id', v ? Number(v) : null)} />
            )}
            {formData.standar_spmi_id && filteredIndikator.length === 0 && <p className="text-sm text-neutral-text-secondary italic">Tidak ada indikator untuk standar ini.</p>}
          </div>
        )}
        {currentStep === 3 && (
          <div className="space-y-6">
            <div className="flex items-center gap-2 text-teal-dark mb-2"><AlertTriangle className="w-5 h-5" /><h2 className="text-lg font-bold">Detail Masalah</h2></div>
            <Textarea label="Deskripsi Masalah" placeholder="Jelaskan masalah secara detail..." value={formData.deskripsi} onChange={(e) => updateField('deskripsi', e.target.value)} error={errors.deskripsi} required maxLength={5000} showCount rows={6} />
            <Textarea label="Dampak Masalah" placeholder="Apa dampak dari masalah ini?" value={formData.dampak} onChange={(e) => updateField('dampak', e.target.value)} maxLength={3000} showCount rows={4} />
            <Textarea label="Akar Masalah" placeholder="Apa penyebab utama dari masalah ini?" value={formData.akar_masalah} onChange={(e) => updateField('akar_masalah', e.target.value)} maxLength={3000} showCount rows={4} />
          </div>
        )}
        {currentStep === 4 && (
          <div className="space-y-6">
            <div className="flex items-center gap-2 text-teal-dark mb-2"><Paperclip className="w-5 h-5" /><h2 className="text-lg font-bold">Dokumen Pendukung</h2></div>
            <p className="text-sm text-neutral-text-secondary">Upload dokumen pendukung (opsional). Maks 10 file, 10MB per file.</p>
            <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-neutral-border rounded-xl hover:border-teal-light hover:bg-teal-main/5 transition-all cursor-pointer">
              <Upload className="w-8 h-8 text-neutral-text-secondary mb-2" /><p className="text-sm font-medium">Klik untuk upload atau drag & drop</p><p className="text-xs text-neutral-text-secondary mt-1">PDF, DOC, DOCX, XLS, XLSX, JPG, PNG (Maks 10MB)</p>
              <input type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" onChange={handleFileAdd} className="hidden" />
            </label>
            {files.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">File Terupload ({files.length}/10)</p>
                {files.map((file) => (
                  <div key={file.id} className="flex items-center gap-3 p-3 bg-neutral-bg rounded-lg">
                    {file.preview ? <img src={file.preview} alt="" className="w-10 h-10 rounded object-cover" /> : <div className="w-10 h-10 bg-teal-main/10 rounded flex items-center justify-center"><FileText className="w-5 h-5 text-teal-main" /></div>}
                    <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{file.name}</p><p className="text-xs text-neutral-text-secondary">{formatFileSize(file.size)}</p></div>
                    <button onClick={() => removeFile(file.id)} className="p-1.5 rounded-lg hover:bg-danger/10 text-neutral-text-secondary hover:text-danger transition-colors"><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {currentStep === 5 && (
          <div className="space-y-6">
            <div className="flex items-center gap-2 text-teal-dark mb-2"><Eye className="w-5 h-5" /><h2 className="text-lg font-bold">Klasifikasi & Review</h2></div>
            <div className="space-y-2">
              <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">Tingkat Risiko <span className="text-danger">*</span></label>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {(['rendah', 'sedang', 'tinggi', 'kritis'] as TingkatRisiko[]).map((risk) => {
                  const config = risikoLabels[risk];
                  const isSelected = formData.tingkat_risiko === risk;
                  return (
                    <button key={risk} onClick={() => handleRiskChange(risk)}
                      className="p-4 rounded-xl border-2 text-left transition-all"
                      style={{
                        backgroundColor: isSelected ? config.activeBg : config.bg,
                        borderColor: isSelected ? config.activeBorder : config.border,
                        boxShadow: isSelected ? '0 4px 6px -1px rgba(0,0,0,0.1)' : 'none',
                      }}>
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: config.dot }} />
                        <Badge variant="risk" risk={risk} size="md" />
                      </div>
                      <p className="text-xs text-neutral-text-secondary mt-2">SLA: {config.sla}</p>
                    </button>
                  );
                })}
              </div>
              {errors.tingkat_risiko && <p className="text-xs text-danger">{errors.tingkat_risiko}</p>}
            </div>
            <Input label="Target Penyelesaian" type="date" value={formData.target_selesai} onChange={(e) => updateField('target_selesai', e.target.value)} error={errors.target_selesai} required min={getBesokDateForInput()} />
            <div className="mt-8 p-6 bg-white rounded-xl border border-neutral-border shadow-sm">
              <h3 className="text-base font-bold text-teal-dark mb-5 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Ringkasan Laporan
              </h3>

              {/* Row items with subtle dividers */}
              <div className="divide-y divide-neutral-border/60">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 pb-4">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary/70 mb-0.5">Kategori</p>
                    <p className="text-sm font-semibold text-neutral-text-primary">{selectedKategori?.nama || '-'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary/70 mb-0.5">Standar SPMI</p>
                    <p className="text-sm font-semibold text-neutral-text-primary">{selectedStandar ? `${selectedStandar.kode} - ${selectedStandar.nama}` : '-'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 py-4">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary/70 mb-0.5">Indikator</p>
                    <p className="text-sm font-semibold text-neutral-text-primary">{selectedIndikator ? `${selectedIndikator.kode} - ${selectedIndikator.nama}` : '-'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary/70 mb-0.5">Lampiran</p>
                    <p className="text-sm font-semibold text-neutral-text-primary">{files.length} file</p>
                  </div>
                </div>

                <div className="py-4">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary/70 mb-0.5">Judul</p>
                  <p className="text-sm font-semibold text-neutral-text-primary">{formData.judul || '-'}</p>
                </div>

                {/* Deskripsi in its own highlighted block */}
                <div className="py-4">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary/70 mb-1.5">Deskripsi</p>
                  <div className="p-3 bg-neutral-bg rounded-lg">
                    <p className="text-sm text-neutral-text-primary leading-relaxed line-clamp-4">{formData.deskripsi || '-'}</p>
                  </div>
                </div>

                {/* Risk + Target in a visually distinct row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 pt-4">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary/70 mb-1">Tingkat Risiko</p>
                    <div>{formData.tingkat_risiko ? <Badge variant="risk" risk={formData.tingkat_risiko as TingkatRisiko} size="md" /> : '-'}</div>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary/70 mb-1">Target Selesai</p>
                    <p className="text-sm font-semibold text-neutral-text-primary">{formData.target_selesai ? new Date(formData.target_selesai).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>
      <div className="flex items-center justify-between">
        <div>{currentStep > 1 && <Button variant="outline" onClick={prevStep} icon={<ChevronLeft className="w-4 h-4" />}>Sebelumnya</Button>}</div>
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={handleSaveDraft} loading={saving} icon={<Save className="w-4 h-4" />}>Simpan Draft</Button>
          {currentStep < 5 ? (
            <Button variant="primary" onClick={nextStep} icon={<ChevronRight className="w-4 h-4" />}>Selanjutnya</Button>
          ) : (
            <Button variant="primary" onClick={handleSubmit} loading={submitting} icon={<Send className="w-4 h-4" />}>Submit Laporan</Button>
          )}
        </div>
      </div>

      <Modal
        isOpen={showValidationModal}
        onClose={() => setShowValidationModal(false)}
        title="Periksa kembali isian formulir"
        footer={<Button variant="outline" onClick={() => setShowValidationModal(false)}>Tutup</Button>}
      >
        <p className="text-sm text-neutral-text-secondary mb-3">
          Ada beberapa bagian yang belum sesuai. Silakan perbaiki poin berikut:
        </p>
        <ul className="space-y-2 text-sm list-disc pl-5">
          {validationMessages.map((message, index) => (
            <li key={`${index}-${message}`}>{message}</li>
          ))}
        </ul>
      </Modal>
    </div>
  );
};

export default LaporanCreate;
